import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import API_BASE_URL from '../utils/config';

// Toolbar component for Tiptap
const Toolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="tiptap-toolbar">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        Bold
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        Italic
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      >
        Strike
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      >
        Bullet List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      >
        Ordered List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
      >
        Blockquote
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
      >
        Code Block
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleSubscript().run()}
        className={editor.isActive('subscript') ? 'is-active' : ''}
      >
        Subscript
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
        className={editor.isActive('superscript') ? 'is-active' : ''}
      >
        Superscript
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
      >
        Align Left
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
      >
        Align Center
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
      >
        Align Right
      </button>
      <button
        type="button"
        onClick={() => {
          const url = prompt('Enter the URL');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        className={editor.isActive('link') ? 'is-active' : ''}
      >
        Link
      </button>
      <button
        type="button"
        onClick={() => {
          const url = prompt('Enter image URL');
          if (url) editor.chain().focus().setImage({ src: url }).run();
        }}
      >
        Image
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        Insert Table
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().addRowBefore().run()}
        disabled={!editor.can().addRowBefore()}
      >
        Add Row Before
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().addRowAfter().run()}
        disabled={!editor.can().addRowAfter()}
      >
        Add Row After
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().addColumnBefore().run()}
        disabled={!editor.can().addColumnBefore()}
      >
        Add Column Before
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        disabled={!editor.can().addColumnAfter()}
      >
        Add Column After
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteRow().run()}
        disabled={!editor.can().deleteRow()}
      >
        Delete Row
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteColumn().run()}
        disabled={!editor.can().deleteColumn()}
      >
        Delete Column
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().deleteTable().run()}
        disabled={!editor.can().deleteTable()}
      >
        Delete Table
      </button>
      <select
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
      >
        <option value="">Default Color</option>
        <option value="#ff0000">Red</option>
        <option value="#00ff00">Green</option>
        <option value="#0000ff">Blue</option>
      </select>
      <button
        type="button"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        Clear Format
      </button>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    image_url: ''
  });

  // CSS for table styling and content formatting
  const contentStyles = `
    /* Editor styles */
    .ProseMirror {
      min-height: 400px;
      padding: 1rem;
      background: white;
      border-radius: 0.375rem;
      border: 1px solid #e5e7eb;
    }

    .ProseMirror > * + * {
      margin-top: 1rem;
    }

    .ProseMirror p {
      margin: 1rem 0;
      line-height: 1.6;
    }

    .ProseMirror ul, .ProseMirror ol {
      padding-left: 2rem;
      margin: 1rem 0;
    }

    .ProseMirror ul {
      list-style-type: disc;
    }

    .ProseMirror ol {
      list-style-type: decimal;
    }

    .ProseMirror li {
      margin: 0.5rem 0;
    }

    /* Table styles */
    .ProseMirror table {
      width: 100%;
      border-collapse: collapse;
      margin: 1.5rem 0;
      overflow-x: auto;
      display: block;
    }

    .ProseMirror table td, .ProseMirror table th {
      border: 1px solid #ddd;
      padding: 12px;
      text-align: left;
    }

    .ProseMirror table th {
      background-color: #f2f2f2;
      font-weight: 600;
    }

    .ProseMirror table tr:nth-child(even) {
      background-color: #f9f9f9;
    }

    .ProseMirror table tr:hover {
      background-color: #f1f1f1;
    }

    /* Preview styles */
    .prose {
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
    }

    .prose p {
      margin: 1.5rem 0;
      line-height: 1.8;
      text-align: justify;
    }

    .prose ul, .prose ol {
      padding-left: 2.5rem;
      margin: 1.5rem 0;
    }

    .prose ul {
      list-style-type: disc;
    }

    .prose ol {
      list-style-type: decimal;
    }

    .prose li {
      margin: 0.75rem 0;
      line-height: 1.6;
    }

    .prose table {
      width: 100%;
      border-collapse: collapse;
      margin: 2rem 0;
      overflow-x: auto;
      display: block;
    }

    .prose table td, .prose table th {
      border: 1px solid #ddd;
      padding: 12px;
      text-align: left;
    }

    .prose table th {
      background-color: #f2f2f2;
      font-weight: 600;
    }

    .prose table tr:nth-child(even) {
      background-color: #f9f9f9;
    }

    .prose table tr:hover {
      background-color: #f1f1f1;
    }

    .prose img {
      max-width: 100%;
      height: auto;
      margin: 2rem auto;
      display: block;
      border-radius: 0.5rem;
    }

    .prose blockquote {
      border-left: 4px solid #ddd;
      padding-left: 1rem;
      margin: 1.5rem 0;
      color: #666;
    }

    .prose code {
      background-color: #f5f5f5;
      padding: 0.2rem 0.4rem;
      border-radius: 0.25rem;
      font-family: monospace;
    }

    .prose pre {
      background-color: #f5f5f5;
      padding: 1rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 1.5rem 0;
    }

    /* Toolbar styles */
    .tiptap-toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px;
      background-color: #f4f4f4;
      border-bottom: 1px solid #ddd;
      margin-bottom: 8px;
      border-radius: 0.375rem 0.375rem 0 0;
    }

    .tiptap-toolbar button {
      padding: 6px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #fff;
      cursor: pointer;
      transition: all 0.2s;
    }

    .tiptap-toolbar button:hover {
      background-color: #e0e0e0;
    }

    .tiptap-toolbar button.is-active {
      background-color: #d0d0d0;
    }

    .tiptap-toolbar button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .tiptap-toolbar select {
      padding: 6px;
      border: 1px solid #ddd;
      border-radius: 4px;
    }
  `;

  // Initialize Tiptap editor with updated configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        paragraph: {
          HTMLAttributes: {
            class: 'my-paragraph',
          },
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'my-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'table'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'my-link',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'my-image',
        },
      }),
      CodeBlock.configure({
        HTMLAttributes: {
          class: 'my-code-block',
        },
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: 'my-blockquote',
        },
      }),
      Subscript,
      Superscript,
      Color,
      TextStyle,
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({
        ...prev,
        content: editor.getHTML(),
      }));
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchBlogs();
  }, [navigate]);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs`);
      if (!response.ok) {
        throw new Error('Failed to fetch blogs');
      }
      const data = await response.json();
      setBlogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/blogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa('admin:mayaadmin@2025'),
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to create blog');
      }

      setShowAddForm(false);
      setShowPreview(false);
      setFormData({
        title: '',
        description: '',
        content: '',
        image_url: '',
      });
      editor.commands.setContent('');
      fetchBlogs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa('admin:mayaadmin@2025'),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete blog');
      }

      fetchBlogs();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    navigate('/admin/login');
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-700"></div>
      </div>
    );
  }

  const BlogPreview = () => {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Blog Preview</h2>
          <button 
            onClick={togglePreview}
            className="text-gray-600 hover:text-gray-800 px-3 py-1 border border-gray-300 rounded"
          >
            Close Preview
          </button>
        </div>
        
        {formData.image_url && (
          <div className="mb-4">
            <img 
              src={formData.image_url} 
              alt={formData.title} 
              className="w-full h-64 object-cover rounded"
              onError={(e) => (e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found')}
            />
          </div>
        )}
        
        <h1 className="text-3xl font-bold mb-4">{formData.title}</h1>
        
        <div className="text-gray-600 mb-6">{formData.description}</div>
        
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formData.content }} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 mt-40">
      <style>{contentStyles}</style>

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <div className="flex gap-4">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              {showAddForm ? 'Cancel' : 'Add New Blog'}
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {showPreview && <BlogPreview />}

        {showAddForm && !showPreview && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Add New Blog</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Content</label>
                <div className="mt-1">
                  <Toolbar editor={editor} />
                  <EditorContent editor={editor} className="border border-gray-300 rounded-md p-4" style={{ minHeight: '400px' }} />
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Tip: Use the table button to insert a table. Use additional buttons to manage rows and columns.
                </div>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700">Image URL</label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div> */}
              <div className="flex justify-between pt-8">
                <button
                  type="button"
                  onClick={togglePreview}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Preview Blog
                </button>
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Create Blog
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          {blogs.map((blog) => (
            <div key={blog._id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{blog.title}</h3>
                    <p className="mt-1 text-sm text-gray-500">{blog.description}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(blog._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  Created: {new Date(blog.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;